<template>
  <div class="bg-white shadow-md rounded-xl p-6 h-full flex flex-col">
    <h2 class="text-xl font-semibold text-gray-800 border-b pb-4">Guest Management</h2>
    <div class="mt-4 space-y-6 flex-grow">
      <div>
        <h3 class="text-lg font-medium text-gray-900">Pending Guest Signature</h3>
        <!-- START: CORRECTED GUEST LIST BACKGROUND -->
        <div class="mt-2 bg-brand-yellow/10 border border-brand-yellow/20 rounded-xl p-4 space-y-3 min-h-[120px]">
        <!-- END: CORRECTED GUEST LIST BACKGROUND -->
          <div v-if="loading.guest" class="space-y-3 animate-pulse"><div v-for="i in 2" :key="i" class="p-3 bg-white rounded-lg shadow-sm h-16"></div></div>
          <div v-else-if="pendingGuestSubmissions.length === 0" class="text-center text-gray-500 h-full flex flex-col justify-center items-center py-4">
            <svg class="h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"></path></svg>
            <p class="mt-2 font-semibold text-gray-700">No invites pending</p><p class="text-sm">Use the "Invite New Guest" panel to begin.</p>
          </div>
          <a v-else v-for="submission in pendingGuestSubmissions" :key="submission.id" :href="getGuestGuideUrl(submission.id)" target="_blank" class="p-3 bg-white rounded-lg shadow-sm block hover:bg-gray-50 transition-colors duration-200">
            <div class="flex justify-between items-start">
              <div>
                <p class="font-semibold text-gray-700">{{ submission.guestName }}</p>
                <p class="text-xs text-gray-500">Host: {{ submission.createdByName }}</p>
              </div>
              <button @click.prevent.stop="copyInviteLink(submission.id)" class="text-sm font-semibold text-brand-purple-dark hover:text-brand-pink-static focus:outline-none w-24 text-right flex-shrink-0">
                <span v-if="copiedLinkId === submission.id">Copied! ✅</span><span v-else>Copy Link</span>
              </button>
            </div>
            <p v-if="submission.recordingDate" class="mt-2 text-xs text-gray-600 font-medium bg-gray-100 inline-block px-2 py-1 rounded">
              🗓️ {{ formatFullDate(submission.recordingDate) }}
            </p>
          </a>
        </div>
      </div>
      <div>
        <h3 class="text-lg font-medium text-gray-900">Pending Host Signature</h3>
        <!-- START: CORRECTED HOST LIST BACKGROUND -->
        <div class="mt-2 bg-brand-pink-static/10 border border-brand-pink-static/20 rounded-xl p-4 space-y-3 min-h-[120px]">
        <!-- END: CORRECTED HOST LIST BACKGROUND -->
          <div v-if="loading.host" class="space-y-3 animate-pulse"><div v-for="i in 2" :key="i" class="p-3 bg-white rounded-lg shadow-sm h-16"></div></div>
           <div v-else-if="pendingHostSubmissions.length === 0" class="text-center text-gray-500 h-full flex flex-col justify-center items-center py-4">
             <svg class="h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
            <p class="mt-2 font-semibold text-gray-700">All caught up!</p><p class="text-sm">No submissions are waiting for your signature.</p>
          </div>
          <div v-else v-for="submission in pendingHostSubmissions" :key="submission.id" class="p-3 bg-white rounded-lg shadow-sm block">
             <div class="flex justify-between items-start">
              <div>
                <p class="font-semibold text-gray-700">{{ submission.guestName }}</p>
                <p class="text-xs text-gray-500">Host: {{ submission.createdByName }}</p>
              </div>
              <router-link :to="`/submission/${submission.id}`" class="text-sm font-semibold text-brand-purple-dark hover:text-brand-pink-static flex-shrink-0">Review & Sign</router-link>
            </div>
            <p v-if="submission.recordingDate" class="mt-2 text-xs text-gray-600 font-medium bg-gray-100 inline-block px-2 py-1 rounded">
              🗓️ {{ formatFullDate(submission.recordingDate) }}
            </p>
          </div>
        </div>
      </div>
    </div>
    <div class="pt-4 mt-auto">
      <!-- START: CORRECTED BUTTON COLOUR -->
      <router-link to="/submissions" class="block w-full text-center bg-brand-purple-dark text-white py-2 px-4 rounded-md hover:opacity-90">View All Submissions</router-link>
      <!-- END: CORRECTED BUTTON COLOUR -->
    </div>
  </div>
</template>

<script>
import { db } from '@/firebase';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { mapState } from 'vuex';

export default {
  name: 'GuestManagementPanel',
  data() {
    return {
      pendingGuestSubmissions: [], pendingHostSubmissions: [],
      loading: { guest: true, host: true },
      unsubscribeGuest: null, unsubscribeHost: null,
      copiedLinkId: null,
    }
  },
  computed: { ...mapState(['clientId', 'authIsReady']) },
  methods: {
    fetchPendingGuestSubmissions() {
      if (!this.clientId) return;
      const q = query(collection(db, 'submissions'), where('clientId', '==', this.clientId), where('status', '==', 'pending_guest_signature'), orderBy('createdAt', 'desc'));
      this.unsubscribeGuest = onSnapshot(q, (snapshot) => {
        this.pendingGuestSubmissions = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        this.loading.guest = false;
      }, (error) => { console.error("Error fetching pending guest submissions:", error); this.loading.guest = false; });
    },
    fetchPendingHostSubmissions() {
      if (!this.clientId) return;
      const q = query(collection(db, 'submissions'), where('clientId', '==', this.clientId), where('status', '==', 'pending_host_signature'), orderBy('submittedAt', 'desc'));
      this.unsubscribeHost = onSnapshot(q, (snapshot) => {
        this.pendingHostSubmissions = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        this.loading.host = false;
      }, (error) => { console.error("Error fetching pending host submissions:", error); this.loading.host = false; });
    },
    formatDate(timestamp) {
      if (!timestamp?.toDate) return 'Invalid date';
      return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(timestamp.toDate());
    },
    formatFullDate(timestamp) {
      if (!timestamp?.toDate) return 'Date TBC';
      return new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeStyle: 'short' }).format(timestamp.toDate());
    },
    getGuestGuideUrl(submissionId) {
      return `${window.location.origin}/guest-guide/${submissionId}`;
    },
    copyInviteLink(submissionId) {
      if (this.copiedLinkId) return;
      const fullUrl = this.getGuestGuideUrl(submissionId);
      navigator.clipboard.writeText(fullUrl).then(() => {
        this.copiedLinkId = submissionId;
        setTimeout(() => { this.copiedLinkId = null; }, 2500);
      }).catch(err => { console.error('Failed to copy text: ', err); });
    }
  },
  watch: {
    authIsReady(isReady) {
      if (isReady && !this.unsubscribeGuest) {
        this.fetchPendingGuestSubmissions();
        this.fetchPendingHostSubmissions();
      }
    }
  },
  created() {
    if (this.authIsReady) {
      this.fetchPendingGuestSubmissions();
      this.fetchPendingHostSubmissions();
    }
  },
  beforeUnmount() {
    if (this.unsubscribeGuest) this.unsubscribeGuest();
    if (this.unsubscribeHost) this.unsubscribeHost();
  }
}
</script>